import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { ReportData } from '../timecard/timecardType';

interface TimecardReportTableProps {
  reportData: ReportData[];
}

const TimecardReportTable: React.FC<TimecardReportTableProps> = ({ reportData }) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: 'full_name' | 'jobsite_name') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = [...reportData].sort((a, b) => {
    if (!sortColumn) return 0;

    const aValue = sortColumn === 'full_name' ? a.full_name.split(' ').slice(-1)[0] : a.jobsite_name;
    const bValue = sortColumn === 'full_name' ? b.full_name.split(' ').slice(-1)[0] : b.jobsite_name;

    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  if (reportData.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full border-collapse">
        <thead className="bg-gray-50 border-b-2 border-gray-200">
          <tr>
            <th className="text-left px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Employee</span>
                <div className="flex flex-col">
                  <ChevronUp
                    size={14}
                    onClick={() => handleSort('full_name')}
                    className={`cursor-pointer transition-colors ${
                      sortColumn === 'full_name' && sortDirection === 'asc'
                        ? 'text-primary'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  />
                  <ChevronDown
                    size={14}
                    onClick={() => handleSort('full_name')}
                    className={`cursor-pointer -mt-1 transition-colors ${
                      sortColumn === 'full_name' && sortDirection === 'desc'
                        ? 'text-primary'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  />
                </div>
              </div>
            </th>
            <th className="text-left px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Jobsite</span>
                <div className="flex flex-col">
                  <ChevronUp
                    size={14}
                    onClick={() => handleSort('jobsite_name')}
                    className={`cursor-pointer transition-colors ${
                      sortColumn === 'jobsite_name' && sortDirection === 'asc'
                        ? 'text-primary'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  />
                  <ChevronDown
                    size={14}
                    onClick={() => handleSort('jobsite_name')}
                    className={`cursor-pointer -mt-1 transition-colors ${
                      sortColumn === 'jobsite_name' && sortDirection === 'desc'
                        ? 'text-primary'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  />
                </div>
              </div>
            </th>
            <th className="text-left px-4 py-3 font-semibold">Clock In</th>
            <th className="text-left px-4 py-3 font-semibold">Clock Out</th>
            <th className="text-left px-4 py-3 font-semibold">Duration</th>
            <th className="text-left px-4 py-3 font-semibold">Payment ($)</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((timecard, index) => (
            <tr
              key={timecard.timecard_id}
              className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
              }`}
            >
              <td className="px-4 py-3">{timecard.full_name}</td>
              <td className="px-4 py-3">{timecard.jobsite_name}</td>
              <td className="px-4 py-3">{timecard.clock_in}</td>
              <td className="px-4 py-3">{timecard.clock_out}</td>
              <td className="px-4 py-3 font-medium">{timecard.duration}</td>
              <td className="px-4 py-3 font-medium">{timecard.payment || 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TimecardReportTable;